import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  MONTH_NAMES, fmtBRL, fmtPct, apiErrorMessage, fetchCompanies, preferredCompanyId,
} from '../utils/clinicalUtils';
import './clinical.css';

/**
 * ClinicalExpensesPage — CRUD de despesas + DRE do mês.
 * O DRE (receita, lucro, margem) vem PRONTO de GET /monthly — RN-21/22/23.
 * Lucro nunca é digitado. Sem reload após mutação.
 */

const CATEGORY_SUGGESTIONS = ['folha', 'aluguel', 'material', 'marketing', 'impostos', 'não classificado'];

const EMPTY_FORM = { category: '', amount: '', description: '' };

function ClinicalExpensesPage() {
  const now = new Date();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [expenses, setExpenses] = useState([]);
  const [dre, setDre] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length) setCompanyId(preferredCompanyId(list));
      })
      .catch(() => setError('Falha ao carregar as unidades.'));
  }, []);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    try {
      const [expensesRes, monthlyRes] = await Promise.all([
        api.get('/clinical-results/expenses', {
          params: { company_id: companyId, reference_year: year, reference_month: month },
        }),
        api.get('/clinical-results/monthly', {
          params: { company_id: companyId, year, month },
        }),
      ]);
      setExpenses(expensesRes.data || []);
      setDre(monthlyRes.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao carregar despesas.'));
    }
  }, [companyId, year, month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    try {
      const payload = {
        company_id: companyId,
        reference_year: year,
        reference_month: month,
        category: form.category.trim(),
        amount: Number(form.amount),
        description: form.description || null,
      };

      if (editingId) {
        await api.put(`/clinical-results/expenses/${editingId}`, payload);
        setSuccess('Despesa atualizada.');
      } else {
        await api.post('/clinical-results/expenses', payload);
        setSuccess('Despesa lançada.');
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadData(); // recarrega lista + DRE recalculado pelo servidor
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const handleEdit = (expense) => {
    setEditingId(expense.id);
    setForm({
      category: expense.category,
      amount: String(expense.amount),
      description: expense.description || '',
    });
  };

  const handleDelete = async (expense) => {
    if (!window.confirm(`Excluir a despesa '${expense.category}' de ${fmtBRL(expense.amount)}?`)) return;
    setError('');
    try {
      await api.delete(`/clinical-results/expenses/${expense.id}`);
      setSuccess('Despesa excluída.');
      if (editingId === expense.id) {
        setEditingId(null);
        setForm(EMPTY_FORM);
      }
      await loadData();
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  return (
    <div className="clinical-page">
      <h1>Despesas e DRE — Resultados da Unidade</h1>
      <p className="clinical-subtitle">O lucro líquido é sempre calculado: receita do mês menos despesas lançadas.</p>

      {error && <div className="clinical-error">{error}</div>}
      {success && <div className="clinical-success">{success}</div>}

      <div className="clinical-filters">
        <label>
          Unidade
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>
          Mês
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
          </select>
        </label>
        <label>
          Ano
          <input type="number" min="2020" max="2100" value={year} onChange={(e) => setYear(Number(e.target.value))} />
        </label>
      </div>

      {dre && (
        <div className="clinical-dre">
          <div className="dre-line">
            <span>Receita total do mês</span>
            <strong>{fmtBRL(dre.total_revenue)}</strong>
          </div>
          <div className="dre-line">
            <span>(−) Despesas</span>
            <strong>{fmtBRL(dre.total_expenses)}</strong>
          </div>
          <div className="dre-line dre-total">
            <span>Lucro líquido</span>
            <strong>{fmtBRL(dre.net_profit)}</strong>
          </div>
          <div className="dre-line dre-total">
            <span>Margem líquida</span>
            <strong>{fmtPct(dre.net_margin)}</strong>
          </div>
        </div>
      )}

      <h2 className="clinical-section-title">{editingId ? 'Editar despesa' : 'Nova despesa'}</h2>
      <div className="clinical-form">
        <label>
          Categoria
          <input
            list="expense-categories"
            name="category"
            value={form.category}
            onChange={handleChange}
            placeholder="Ex.: folha, aluguel..."
          />
          <datalist id="expense-categories">
            {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
          </datalist>
        </label>
        <label>
          Valor (R$)
          <input type="number" min="0" step="0.01" name="amount" value={form.amount} onChange={handleChange} />
        </label>
        <label className="form-full">
          Descrição (opcional)
          <input type="text" name="description" value={form.description} onChange={handleChange} />
        </label>
        <div className="clinical-form-actions">
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={!companyId || !form.category.trim() || form.amount === ''}
          >
            {editingId ? 'Atualizar despesa' : 'Lançar despesa'}
          </button>
          {editingId && (
            <button className="btn-secondary" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}>
              Cancelar
            </button>
          )}
        </div>
      </div>

      {expenses.length === 0 ? (
        <p className="clinical-empty">Nenhuma despesa lançada no período.</p>
      ) : (
        <table className="clinical-table">
          <thead>
            <tr>
              <th>Categoria</th>
              <th className="num">Valor</th>
              <th>Descrição</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id}>
                <td>{e.category}</td>
                <td className="num">{fmtBRL(e.amount)}</td>
                <td>{e.description || '—'}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleEdit(e)}>Editar</button>{' '}
                  <button className="btn-danger" onClick={() => handleDelete(e)}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ClinicalExpensesPage;
